import React from "react";
import clsx from "clsx";
import { AutocompleteRenderInputParams } from "@mui/material/Autocomplete";
import { PopperProps, Popper } from "@mui/material";

import Autocomplete from "../Autocomplete";
import TextField from "../TextField";
import FieldGroup from "../FieldGroup";
import { PrefixTextFieldProps } from "./PrefixTextField.types";
import IconField from "../IconField";
import TextFieldBase from "../TextFieldBase";
import { useStyles } from "./PrefixTextField.styles";

function PrefixTextField(props: PrefixTextFieldProps): JSX.Element {
  const {
    disabled,
    error,
    getPrefixOptionDescription,
    getPrefixOptionIcon,
    getPrefixOptionValue,
    helperText,
    id,
    onPrefixBlur,
    onPrefixChange,
    prefix = "",
    prefixFilterOptions,
    prefixLabel,
    prefixOptions,
    prefixRequired,
    prefixWidth,
    size,
    inputRef,
    prefixInputRef,
    showHelperTextMargin,
    ...other
  } = props;

  const classes = useStyles();

  const getIcon = (value: any) => {
    if (!getPrefixOptionValue || !getPrefixOptionIcon) {
      return null;
    }

    const option = prefixOptions.find((o) => getPrefixOptionValue(o) === value);
    if (!option) {
      return null;
    }

    return getPrefixOptionIcon(option);
  };

  const prefixValue = React.useMemo(
    () =>
      prefixOptions?.find((option) => getPrefixOptionValue(option) === prefix),
    [getPrefixOptionValue, prefix, prefixOptions]
  );

  const handlePrefixChange = (_: React.ChangeEvent<any>, option: any) => {
    if (onPrefixChange) {
      const nextPrefix = getPrefixOptionValue(option) || "";
      onPrefixChange(nextPrefix);
    }
  };

  const prefixOptionStyle = {
    width: prefixWidth,
  };

  // MUI v5 changed renderOption to `(props, option, state)` (v4 was
  // `(option, state)`). `props` MUST be spread onto the option element to get
  // role="option", the key and the click/hover handlers — otherwise the
  // dropdown renders unusable (no selectable options, blank text).
  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement>,
    option: any
  ) => (
    <li
      {...optionProps}
      className={clsx(optionProps.className, classes.optionRoot)}
    >
      {getPrefixOptionIcon ? (
        <span style={prefixOptionStyle} className={classes.optionIcon}>
          {getPrefixOptionIcon(option)}
        </span>
      ) : (
        <span style={prefixOptionStyle} className={classes.optionValue}>
          {getPrefixOptionValue(option)}
        </span>
      )}
      <span className={classes.optionDescription}>
        {getPrefixOptionDescription(option)}
      </span>
    </li>
  );

  const renderInput = (params: AutocompleteRenderInputParams) => {
    // MUI v5 wires the dropdown open-interaction, popup indicator and input-root
    // ref onto `params.InputProps`. v4 kept them on the input element, so the old
    // code could drop InputProps and only forward its `ref`. Under v5 that breaks
    // the dropdown (clicking never opens it), so pass `params` through intact.
    return getPrefixOptionIcon ? (
      <IconField
        {...params}
        className={classes.prefixInput}
        disabled={disabled}
        label={prefixLabel}
        required={prefixRequired}
        getIcon={getIcon}
        inputRef={prefixInputRef}
        error={error}
      />
    ) : (
      <TextFieldBase
        {...params}
        className={classes.prefixInput}
        disabled={disabled}
        label={prefixLabel}
        required={prefixRequired}
        inputRef={prefixInputRef}
        error={error}
      />
    );
  };

  const fieldGroupContentRef = React.useRef<HTMLDivElement>(null);

  const PopperComponent = (props: PopperProps) => {
    const width = fieldGroupContentRef.current?.clientWidth;
    return (
      <Popper
        {...props}
        anchorEl={fieldGroupContentRef.current}
        style={{ width }}
      />
    );
  };

  return (
    <FieldGroup
      prefixWidth={prefixWidth}
      error={error}
      helperText={helperText}
      size={size}
      contentRef={fieldGroupContentRef}
      showHelperTextMargin={showHelperTextMargin}
    >
      <Autocomplete
        value={prefixValue}
        disabled={disabled}
        filterOptions={prefixFilterOptions}
        fullWidth
        getOptionLabel={getPrefixOptionValue}
        isOptionEqualToValue={(option: any, value: any) =>
          getPrefixOptionValue(option) === getPrefixOptionValue(value)
        }
        id={id ? `${id}-prefix` : undefined}
        onBlur={onPrefixBlur}
        onChange={handlePrefixChange}
        options={prefixOptions}
        renderInput={renderInput}
        renderOption={renderOption}
        PopperComponent={PopperComponent}
      />
      <TextField {...other} disabled={disabled} inputRef={inputRef} />
    </FieldGroup>
  );
}

export default PrefixTextField;
